import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import * as gdsApi from "../services/gds";
import {
  showErrorNotification,
  showSuccessNotification,
} from "./notifications";

const gdsOfficesAdapter = createEntityAdapter({
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

const initialState = {
  offices: {
    ...gdsOfficesAdapter.getInitialState(),
    byCustomerId: {},
    inProgress: false,
  },
};

const setByCustomerId = (index, o) =>
  o.customerId
    ? {
        ...index,
        [o.customerId]: [...(index[o.customerId] || []), o.id],
      }
    : index;

const isOfType = (actions) => {
  const types = new Set(actions.map((a) => a?.type));
  return (action) => types.has(action.type);
};

const gdsSlice = createSlice({
  name: "gds",
  initialState,
  reducers: {
    setGdsOffices: (state, { payload: gdsOffices }) => {
      state.offices.byCustomerId = gdsOffices.reduce(setByCustomerId, {});
      gdsOfficesAdapter.setAll(state.offices, gdsOffices);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addGdsOffice.fulfilled, (state, { payload: gdsOffice }) => {
        state.offices.byCustomerId = setByCustomerId(
          state.offices.byCustomerId,
          gdsOffice,
        );
        gdsOfficesAdapter.setOne(state.offices, gdsOffice);
        state.offices.inProgress = false;
      })
      .addCase(deleteGdsOffice.fulfilled, (state, { payload: gdsOffice }) => {
        const byCustomerId = new Set(
          state.offices.byCustomerId[gdsOffice.customerId] || [],
        );
        byCustomerId.delete(gdsOffice.id);
        state.offices.byCustomerId[gdsOffice.customerId] = [...byCustomerId];
        gdsOfficesAdapter.removeOne(state.offices, gdsOffice?.id || "");
        state.offices.inProgress = false;
      })
      .addMatcher(
        isOfType([addGdsOffice.pending, deleteGdsOffice.pending]),
        (state) => {
          state.offices.inProgress = true;
        },
      )
      .addMatcher(
        isOfType([addGdsOffice.rejected, deleteGdsOffice.rejected]),
        (state) => {
          state.offices.inProgress = false;
        },
      );
  },
});

export default gdsSlice.reducer;

export const { setGdsOffices } = gdsSlice.actions;

export const isValidGdsId = (gdsId) =>
  gdsId?.iata &&
  /^[0-9]{8,9}$/.test(gdsId?.iata) &&
  gdsId?.officeId &&
  /^(([A-Z0-9]{3,4})|([A-Z0-9]{9}))$/.test(gdsId?.officeId);

export const selectors = gdsOfficesAdapter.getSelectors(
  (state) => state.gds.offices,
);

export const selectGdsIdsByCustomerIdMap = (state) =>
  state?.gds?.offices?.byCustomerId || {};

export const selectGdsIdsByCustomerId = createSelector(
  selectGdsIdsByCustomerIdMap,
  (_, customerId) => customerId,
  (byCustomerId, customerId) => byCustomerId[customerId] || [],
);

export const selectGdsOfficesByCustomerId = createSelector(
  (state) => state,
  selectGdsIdsByCustomerId,
  (state, gdsIds) => gdsIds.map((id) => selectors.selectById(state, id)),
);

export const selectGdsOfficeHQs = createSelector(
  selectors.selectAll,
  (gdsOffices) => gdsOffices.filter(({ officeId }) => officeId === "_HQ"),
);

export const selectGdsOfficeInProgress = (state) =>
  state.gds.offices.inProgress;

const API_ERRORS = {
  400: "You provided incomplete or invalid GDS ID",
  409: "Such GDS ID already exists",
};

export const addGdsOffice = createAsyncThunk(
  "/gds/offices/add",
  (gdsOffice, { dispatch }) =>
    gdsApi
      .saveGdsOffice(gdsOffice)
      .then((savedGdsOffice) => {
        dispatch(showSuccessNotification("GDS ID added successfully"));
        return savedGdsOffice;
      })
      .catch((err) => {
        const message = API_ERRORS[err.status] || err.message;
        dispatch(showErrorNotification(message));
        throw new Error(`${err.status}: ${message}`);
      }),
);

export const loadGdsOffices = createAsyncThunk(
  "gds/offices/loadAll",
  (_, { dispatch }) =>
    gdsApi.findGdsOffices().then((gdsOffices) => {
      dispatch(setGdsOffices(gdsOffices));
    }),
);

export const deleteGdsOffice = createAsyncThunk(
  "gds/offices/delete",
  (gdsOffice, { dispatch }) =>
    gdsApi
      .deleteGdsOffice(gdsOffice?.id)
      .then(() => {
        dispatch(showSuccessNotification("GDS ID removed successfully"));
        return gdsOffice;
      })
      .catch((err) => {
        const message = API_ERRORS[err.status] || err.message;
        dispatch(showErrorNotification(message));
        throw new Error(`${err.status}: ${message}`);
      }),
);
